import React, { Fragment, useEffect } from "react";
import { Divider, Grid, TextField, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { styles } from "./styles";
import { IRestaurant, IRestaurantEdit } from "./interfaces";
import { isEmptyString, maxLength } from "../../utils/utils";

interface IProps {
  restaurant: IRestaurant;
  restaurantEdit: IRestaurantEdit;
  errors: object;
  onChange(
    name: string,
    value: string,
    valid: boolean,
    errorMessage: string,
  ): void;
  handleError(name: string, isValid: boolean, message: string): void;
}

const editFields = [
  'addressLine1',
  'addressLine2',
  'landmark',
  'streetCornerLocation',
  'city',
];

const validate = (name, value) => {
  let valid: boolean = maxLength(value);
  let message: string = 'invalidMaxLength';

  if ((name === 'addressLine1' || name === 'city') && valid) {
    valid = !isEmptyString(value);
    message = 'noEmptyField';
  }

  return { valid, message };
};

export const RestaurantEditInfo: React.FC<IProps> = ({
  restaurant,
  restaurantEdit,
  onChange,
  errors,
  handleError,
}) => {
  const classes = styles();
  const { t } = useTranslation();
  const name = `${restaurant.physical_address_address_line1} ${restaurant.City}`;

  useEffect(() => {
    editFields.forEach(field => {
      const { valid, message } = validate(field, restaurantEdit[field]);
      handleError(field, valid, t(`app.common.${message}`));
    });
  }, []);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const { valid, message } = validate(name, value);
    onChange(name, value, valid, t(`app.common.${message}`));
  };

  return (
    <Fragment>
      <Typography variant="subtitle1">
        {t('restaurant.info.restaurantInformation')}
      </Typography>

      <Grid container className={classes.restaurantInfoTop}>
        <Grid item sm={12} md={8}>
          <div className={classes.col}>
            <div className={classes.textRow}>
              <Typography className={classes.fakeLabel}>
                {t('restaurant.info.edit.restaurantName')}
              </Typography>
              <Typography>{`${name.substring(0, 35)}...`}</Typography>
            </div>
            <div className={classes.textRow}>
              <Typography className={classes.fakeLabel}>
                {t('restaurant.home.franchiseGroup')}
              </Typography>
              <Typography>{restaurant.FranchiseGroup}</Typography>
            </div>
          </div>
        </Grid>
        <Grid item sm={12} md={4}>
          <div className={classes.textRow}>
            <Typography className={classes.fakeLabel}>
              {t('restaurant.info.status')}
            </Typography>
            <Typography>{restaurant.RestaurantStatus}</Typography>
          </div>
          <div className={classes.textRow}>
            <Typography className={classes.fakeLabel}>
              {t('restaurant.info.restaurantNumber')}
            </Typography>
            <Typography>{restaurant.RestaurantNumber}</Typography>
          </div>
        </Grid>
      </Grid>

      <Divider />

      <div className={classes.block}>
        {editFields.map(field => {
          const tName =
            field === 'streetCornerLocation' ? 'crossStreet' : field;
          const isRequired =
            field === 'addressLine1' || field === 'city' ? '*' : '';
          const prefix = field.indexOf('addressLine') >= 0 ? '.edit' : '';
          return (
            <TextField
              key={field}
              margin="dense"
              id={field}
              label={`${t(`restaurant.info${prefix}.${tName}`)}${isRequired}`}
              name={field}
              fullWidth
              className={classes.inputClean}
              variant="outlined"
              value={restaurantEdit[field]}
              onChange={handleChange}
              error={typeof errors[field] !== 'undefined'}
              helperText={errors[field]}
            />
          );
        })}
      </div>

      <Divider />

      <div className={classes.block}>
        <Grid container>
          <Grid item sm={12} md={6}>
            <div className={classes.col}>
              <div className={classes.textRow}>
                <Typography className={classes.fakeLabel}>
                  {t('restaurant.info.timezone')}
                </Typography>
                <Typography>{restaurant.TimeZone}</Typography>
              </div>
              <div className={classes.textRow}>
                <Typography className={classes.fakeLabel}>
                  {t('restaurant.info.region')}
                </Typography>
                <Typography>{restaurant.Region}</Typography>
              </div>
              <div className={classes.textRow}>
                <Typography className={classes.fakeLabel}>
                  {t('restaurant.info.state')}
                </Typography>
                <Typography>{restaurant['State/Province']}</Typography>
              </div>
            </div>
          </Grid>
          <Grid item sm={12} md={6}>
            <Grid container>
              <Grid item sm={6}>
                <div className={classes.textRow}>
                  <Typography className={classes.fakeLabel}>
                    {t('restaurant.info.edit.latitude')}
                  </Typography>
                  <Typography>{restaurant.Latitude}</Typography>
                </div>
              </Grid>
              <Grid item sm={6}>
                <div className={classes.textRow}>
                  <Typography className={classes.fakeLabel}>
                    {t('restaurant.info.edit.longitude')}
                  </Typography>
                  <Typography>{restaurant.Longitude}</Typography>
                </div>
              </Grid>
            </Grid>
            <div className={classes.textRow}>
              <Typography className={classes.fakeLabel}>
                {t('restaurant.info.country')}
              </Typography>
              <Typography>
                {t(
                  `app.common.countries.${restaurant.Country}`,
                  restaurant.Country,
                )}</Typography>
                        </div>
                    </Grid>
                </Grid>
            </div>
        </Fragment>
    );
}
;
